export { HighlightedText } from './highlighted-text'

export interface Fragment {
  readonly match: boolean
  readonly value: string
}

const lowerCaseFindIndex = (s: string, sub: string, offset?: number) =>
  s.toLowerCase().indexOf(sub.toLowerCase(), offset)

const getMatchDataWithMatch: (props: {
  readonly match: string
  readonly value: string
}) => Fragment[] = ({ match, value }) => {
  const content: Fragment[] = []

  let offsetInValue = 0
  for (let offsetInMatch = 0; offsetInMatch < match.length; offsetInMatch++) {
    const startOfMatched = lowerCaseFindIndex(
      value,
      match.charAt(offsetInMatch),
      offsetInValue
    )

    if (startOfMatched === -1) {
      content.push({
        match: false,
        value: value.slice(offsetInValue)
      })
      return content
    }

    if (startOfMatched > offsetInValue) {
      content.push({
        match: false,
        value: value.slice(offsetInValue, startOfMatched)
      })
    }

    // find the end of the contiguous portion of the match
    let endOfMatched = startOfMatched + 1
    while (true) {
      const nextPosition = offsetInMatch + 1
      if (nextPosition === match.length) {
        break
      }

      if (
        lowerCaseFindIndex(value, match.charAt(nextPosition), endOfMatched) !==
        endOfMatched
      ) {
        break
      }

      offsetInMatch++
      endOfMatched++
    }
    content.push({
      match: true,
      value: value.slice(startOfMatched, endOfMatched)
    })
    offsetInValue = endOfMatched
  }

  if (offsetInValue < value.length) {
    content.push({
      match: false,
      value: value.slice(offsetInValue)
    })
  }

  return content
}

export const getMatchData: (props: {
  readonly match: string
  readonly value: string
}) => Fragment[] = ({ match, value }) => {
  if (!match) {
    return [
      {
        match: false,
        value
      }
    ]
  }

  return getMatchDataWithMatch({ match, value })
}

export function parseMatchDataByStrings(
  fragments: Fragment[],
  ...strings: string[]
) {
  // expand fragments by characters
  const charDatas: Fragment[] = []
  fragments.forEach(({ match, value, ...fragment }) => {
    for (const char of value) {
      charDatas.push({
        ...fragment,
        match,
        value: char
      })
    }
  })

  // group fragments by string
  const groupedByCharFragments: Fragment[][] = []
  let charDataByString
  strings.forEach((string) => {
    charDataByString = []
    string.split('').forEach(() => {
      const charData = charDatas.shift()
      if (charData) {
        charDataByString.push({
          match: charData.match,
          value: charData.value
        })
      }
    })
    groupedByCharFragments.push(charDataByString)
  })

  // group fragments by match, by string
  const final: Fragment[][] = []

  groupedByCharFragments.forEach((fragmentsForString) => {
    let group: Fragment[] = []
    let prevMatch: boolean
    fragmentsForString.forEach((fragment, fragmentIndex) => {
      if (fragmentIndex === 0) {
        group = [fragment]
      } else if (fragment.match === prevMatch) {
        group[group.length - 1] = {
          ...group[group.length - 1],
          value: group[group.length - 1].value + fragment.value
        }
      } else {
        group.push(fragment)
      }
      prevMatch = fragment.match
    })
    final.push(group)
  })

  return final
}
